import { type FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@krea/shared/design-system/Button";
import { Label } from "@krea/shared/design-system/Label";
import { Spinner } from "@krea/shared/design-system/Spinner";
import { Textarea } from "@krea/shared/design-system/Textarea";

import { useAddInternalCommentMutation } from "@/features/lender-applications/hooks/data/use-add-internal-comment.mutation";

import { formatTime } from "../../../utils";
import DetailsContainer from "../../details/container";

const LenderNotes = ({
  lenderApplicationId,
  className,
  lenderInternalNotes,
  lenderInternalCommentsUpdatedAt,
}: {
  lenderApplicationId: number;
  className?: string;
  lenderInternalNotes?: string | null;
  lenderInternalCommentsUpdatedAt?: DateString | null;
}) => {
  const [notes, setNotes] = useState(lenderInternalNotes || "");
  const { mutate: postInternalNotesAction, isPending } =
    useAddInternalCommentMutation();

  useEffect(() => {
    setNotes(lenderInternalNotes || "");
  }, [lenderInternalNotes]);

  const { lenderCompanyId } = useParams<{ lenderCompanyId: string }>();

  const onPostNotes = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (form.checkValidity()) {
      postInternalNotesAction(
        {
          lenderCompanyId: Number(lenderCompanyId),
          lenderApplicationId: lenderApplicationId,
          data: { lenderInternalComment: notes },
        },
        {
          onSuccess: () => {
            toast.success("Note saved successfully");
          },
          onError: () => {
            toast.error("Failed to save note");
          },
        },
      );
    }
  };

  return (
    <DetailsContainer
      background
      className={className}
      title="Lender internal notes"
    >
      <div className="w-full">
        <form id="postInternalNotes" noValidate onSubmit={onPostNotes}>
          <div className="space-y-2">
            <Label htmlFor="lender-notes-textarea">
              Note (not shown to customers)
            </Label>
            <Textarea
              id="lender-notes-textarea"
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
              }}
              placeholder="Enter your note here"
              className="min-h-[80px]"
            />
            {lenderInternalCommentsUpdatedAt && (
              <p className="text-sm text-gray-500">
                Last updated: {formatTime(lenderInternalCommentsUpdatedAt)}
              </p>
            )}
          </div>
          <div className="mt-4 flex justify-end xl:justify-start">
            <Button variant="default" type="submit" disabled={isPending}>
              {isPending && <Spinner className="size-4 text-white" />}{" "}
              Save note
            </Button>
          </div>
        </form>
      </div>
    </DetailsContainer>
  );
};

export { LenderNotes };
